import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { Iconify } from 'src/components';
import dayjs from 'dayjs';

const MILESTONE_ICONS = {
    default: 'eva:award-fill',
    lead: 'eva:people-fill',
    revenue: 'eva:credit-card-fill',
    best: 'eva:trending-up-fill',
    record: 'eva:trending-up-fill',
};

export default function MilestonesSection({ milestones, title }) {
    const getIconForMilestone = (milestoneTitle) => {
        const lowerTitle = milestoneTitle?.toLowerCase() || '';
        if (lowerTitle.includes('lead')) return MILESTONE_ICONS.lead;
        if (lowerTitle.includes('revenue')) return MILESTONE_ICONS.revenue;
        if (lowerTitle.includes('best') || lowerTitle.includes('record')) return MILESTONE_ICONS.best;
        return MILESTONE_ICONS.default;
    };

    const formatDate = (dateString) => dayjs(dateString).format('MMM D, YYYY');

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                mb: 3,
            }}
        >
            <Typography sx={{ fontSize: '20px', fontWeight: 600, mb: 3 }}>
                {title}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {milestones.map((milestone, index) => {
                    const iconName = getIconForMilestone(milestone.title);

                    return (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'flex-start',
                                p: 2,
                                borderRadius: '12px',
                                bgcolor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: '#f1f5f9',
                                    borderColor: '#cbd5e1',
                                },
                            }}
                        >
                            {/* Icon */}
                            <Box
                                sx={{
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '10px',
                                    bgcolor: '#fef3c7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#d97706',
                                    flexShrink: 0,
                                }}
                            >
                                <Iconify icon={iconName} width={22} height={22} />
                            </Box>

                            {/* Content */}
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        color: '#64748b',
                                        mb: 0.25,
                                    }}
                                >
                                    {formatDate(milestone.date)}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        mb: 0.25,
                                        color: '#1e293b',
                                    }}
                                >
                                    {milestone.title}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '13px',
                                        color: '#64748b',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {milestone.description}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {milestones.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#64748b' }}>
                        No milestones recorded yet
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

MilestonesSection.propTypes = {
    milestones: PropTypes.arrayOf(
        PropTypes.shape({
            date: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            description: PropTypes.string.isRequired,
        })
    ).isRequired,
    title: PropTypes.string,
};

MilestonesSection.defaultProps = {
    title: 'Recent Milestones',
};
